import { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiButton,
  MiContentDrawer,
  MiDrawerHeader,
} from "@miplatform/components";
import {
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  BUTTON_VARIANTS,
  COLORS,
  MIBUILD_PAGE_HEADER_HEIGHT,
} from "@miplatform/constants";
import { mdiEmailOutline, mdiEyeOutline } from "@mdi/js";
import { EmailForm, useEmailForm } from "@mibuildweb/components";
import { Box, useMediaQuery } from "@mui/material";

export const OrdersEmailDrawer = memo(
  ({
    open,
    entity,
    entityType,
    templateType,
    onCloseoutDrawerOpen,
    onClose,
  }) => {
    const { classes, theme } = useStyles();
    const viewModel = useEmailForm({
      open,
      entity,
      entityType,
      templateType,
      onCloseoutDrawerOpen,
      onClose: () => onClose(templateType),
    });
    const isMediumScreen = useMediaQuery(theme.breakpoints.down("xl"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const drawerWidth = useMemo(() => {
      if (isSmallScreen) return 440;
      if (isMediumScreen) return 500;

      return 666;
    }, [isMediumScreen, isSmallScreen]);

    const DRAWER_TOP_OFFSET =
      (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
      MIBUILD_PAGE_HEADER_HEIGHT;

    const { actions, recipient, title, color, handlePreviewOpen, handleClose } =
      viewModel;

    const header = (
      <MiDrawerHeader
        title={`To ${recipient}: ${title}`}
        containerStyle={{ borderColor: theme.palette[color].dark }}
        headerStyle={{ backgroundColor: theme.palette[color].light }}
        leftIcon={mdiEmailOutline}
        rightAction={
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            color={COLORS.BLACK}
            size="small"
            className={classes.emailPreviewButton}
            startIcon={{ path: mdiEyeOutline, iconSize: 0.6 }}
            title="View Email Preview"
            onClick={handlePreviewOpen}
          />
        }
      />
    );

    return (
      <MiContentDrawer
        open={open}
        width={drawerWidth}
        onClose={handleClose}
        slotProps={{
          transition: {
            unmountOnExit: true,
          },
        }}
        hideBackdrop
        sx={{
          "& > .MuiPaper-root": {
            top: DRAWER_TOP_OFFSET,
            height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
          },
        }}
      >
        <Box className={classes.content}>
          {header}
          <EmailForm entityType={entityType} viewModel={viewModel} />
          <Box className={classes.actionContainer}>
            <MiActions actions={actions} />
          </Box>
        </Box>
      </MiContentDrawer>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  emailPreviewButton: {
    padding: theme.spacing(0, 2),
    borderColor: theme.palette.common.black,
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.common.white,
  },
  actionContainer: {
    marginTop: "auto",
    position: "sticky",
    bottom: "0",
    minHeight: 57,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1, 4),
    zIndex: theme.zIndex.drawer,
    gap: theme.spacing(4),
    borderTop: `1px solid ${theme.palette.grey.lightAccent}`,
    boxShadow: `0px 2px 12px 0px ${theme.palette.grey.accent}`,
  },
}));
